import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { teamService } from '@/services/team-service';
import type { Role } from '@/types/core/role';
import type { Page } from '@/types/shared/page';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { teamMutationKeys, teamQueryKeys } from './query-keys';

export type AssignTeamRoleArgs = {
  role: Role;
};

/**
 * Hook to assign a role to a team.
 * Optimistically updates the team roles list and polls until the role appears.
 */
export function useAssignTeamRole(tenantId?: UUID, teamId?: UUID) {
  const qc = useQueryClient();

  type Context = {
    previousData?: Array<
      [queryKey: readonly unknown[], data: InfiniteData<Page<Role>> | undefined]
    >;
  };

  return useMutation<Role, Error, AssignTeamRoleArgs, Context>({
    mutationKey: teamMutationKeys.assignTeamRole(tenantId, teamId),
    mutationFn: async ({ role }: AssignTeamRoleArgs) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!teamId) throw new Error('Missing team id');
      if (!role.roleId) throw new Error('Missing role id');

      await teamService.assignTeamRole(tenantId, teamId, role.roleId);

      const teamRole = await pollForResource(() =>
        teamService.findTeamRole(tenantId, teamId, role.roleId)
      );

      return teamRole ?? role;
    },
    onMutate: async ({ role }: AssignTeamRoleArgs) => {
      if (!tenantId || !teamId) return { previousData: [] };

      await qc.cancelQueries({
        queryKey: teamQueryKeys.roleListByTeam(tenantId, teamId),
      });

      const previousData = qc.getQueriesData<InfiniteData<Page<Role>>>({
        queryKey: teamQueryKeys.roleListByTeam(tenantId, teamId),
      });

      qc.setQueriesData<InfiniteData<Page<Role>>>(
        { queryKey: teamQueryKeys.roleListByTeam(tenantId, teamId) },
        (old) => {
          if (!old) return old;
          const roleExists = old.pages.some((page) =>
            page.items.some((item) => item.roleId === role.roleId)
          );
          if (roleExists) return old;

          return {
            ...old,
            pages: old.pages.map((page, index) =>
              index === 0
                ? {
                    ...page,
                    items: [role, ...page.items],
                    total: (page.total ?? 0) + 1,
                  }
                : page
            ),
          };
        }
      );

      return { previousData };
    },
    onError: (_error, _payload, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: teamQueryKeys.roleListByTeam(tenantId, teamId),
        refetchType: 'all',
      });
    },
  });
}
